import { Col, InputGroup } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { useFormContext } from "react-hook-form";
import { useEffect, useState } from "react";

import ExpandableSearch from "components/atoms/ExpandableSearch/ExpandableSearch";
import Button from "components/atoms/Button/Button";

type MultiselectFieldProps = {
  /**
   * Options to be displayed in the multiselect
   */
  options: any;
  /**
   * Name of the field for react-hook-form
   */
  name: string;
  /**
   * Label of the field to be displayed on the form
   */
  label: string;
  /**
   * Description of the field to be displayed on the form
   */
  description: string;
  /**
   * The minimum number of fields that must be checked for the field to be valid
   */
  minSelected?: number;
};

function MultiselectField({ options, name, label, description, minSelected }: MultiselectFieldProps) {
  const {
    register,
    watch,
    setValue,
    clearErrors,
    trigger,
    formState: { errors },
  } = useFormContext();
  const selectedOptions = watch(name);

  const [search, setSearch] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);

  // Handle search input change
  useEffect(() => {
    if (search) {
      const timeoutId = setTimeout(() => {
        const searchResults = options.filter((item: any) => item.label.toLowerCase().includes(search.toLowerCase()));
        setFilteredOptions(searchResults);
      }, 500);
      return () => clearTimeout(timeoutId);
    }
    setFilteredOptions(options);
  }, [search, options]);

  const handleToggleAll = (isChecked: boolean) => {
    setValue(name, Object.fromEntries(Object.keys(selectedOptions).map((key) => [key, isChecked])));
    trigger(name);
  };

  const validateMinSelected = () => {
    if (!minSelected) return true;
    const selectedCount = Object.values(selectedOptions).filter(Boolean).length;
    if (selectedCount >= minSelected) Object.keys(selectedOptions).forEach((fieldKey) => clearErrors(`${name}.${fieldKey}`));
    return selectedCount >= minSelected;
  };

  const renderCheckboxes = () => (
    <div className="payment-location-container">
      {filteredOptions.map((item: any, index: number) => {
        if (item.is_active === false) return null;
        return (
          <div className="mb-2" key={index}>
            <InputGroup className="checkbox bg-transparent mb-3">
              <label htmlFor={item.label} className="d-flex align-items-center cursor-pointer mb-0 w-100">
                <input
                  {...register(`${name}[${item.value}]`, { validate: validateMinSelected })}
                  id={item.label}
                  type="checkbox"
                  className="d-none"
                />
                <span className="border-dark border-solid border-1 mr-2 position-relative br-3 checkbox__box" />
                <span className="text-primary mr-auto">{item.label}</span>
              </label>
            </InputGroup>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="request-row d-flex">
      <Col xs={3}>
        <div className="font-weight-bold font-b1 mb-1">{label}</div>
        <div className="font-b3 text-secondary">{description}</div>
      </Col>
      <Col sm={9} style={{ maxWidth: "384px" }}>
        <div role="presentation">
          <ExpandableSearch
            containerClasses="mb-3"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            alwaysExpanded
          />
          {filteredOptions.length > 0 ? renderCheckboxes() : <span className="text-danger pl-3 pt-5">No Results!</span>}
          <div className="d-flex mt-3">
            <Button onClick={() => handleToggleAll(true)} variant="outline-primary">
              <FormattedMessage id="general.selectAll" defaultMessage="Select all" />
            </Button>
            {Object.values(selectedOptions || {}).some((value) => value) && (
              <Button onClick={() => handleToggleAll(false)} className="ml-3" variant="outline-primary">
                <FormattedMessage id="general.clearAll" defaultMessage="Clear all" />
              </Button>
            )}
          </div>
          {errors[name] && (
            <div className="d-block font-b3 invalid-feedback">
              <FormattedMessage id="general.requiredField" defaultMessage="This is a required field" />
            </div>
          )}
        </div>
      </Col>
    </div>
  );
}

MultiselectField.defaultProps = {
  minSelected: 0,
};

export default MultiselectField;
